"use client";

import { Experience } from "@ninetailed/experience.js-react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, FreeMode, Mousewheel, Navigation, Pagination } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { brochureDowloadCtaClick } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { parseCustomExperience } from "@/lib/experiences";
import { BaseEntry, CustomLink, Media } from "@/types";

import { CardCarouselBottomNavigation } from "../AutogeneratedCarousel/CardCarouselBottomNavigation";
import CardGallery from "./CardGallery";
import "./CardsGalleriesCarousel.css";

SwiperCore.use([Pagination, Navigation]);

export interface GalleryCardEntry extends BaseEntry {
  title?: string;
  description?: string;
  textFont?: "El Messiri" | "Roboto";
  textColor?: string;
  backgroundColor?: string;
  textAlignment?: "top" | "bottom";
  image?: Media;
  video?: Media;
  videoBackupImage?: Media;
  aspectRatio: "3:4" | "4:3";
  gradient?: boolean;
  type: "galleryCardImageVideo" | "galleryCardText" | "galleryCardImageText";
}
export interface CardsGalleriesCarouselEntry extends BaseEntry {
  overtitle?: string;
  logo?: Media;
  brochure?: Media;
  cards: GalleryCardEntry[];
  textColor?: string;
  backgroundColor?: string;
  scroll?: boolean;
  stickyDownloadBrochure?: boolean;
}
interface Props {
  entry: CardsGalleriesCarouselEntry;
  projectInfo?: {
    title?: string;
    name?: string;
    url?: string;
  };
}

// Defined at module level so its identity is stable across parent renders.
// If it were declared inside CardsGalleriesCarousel, every parent render would remount the Swiper and every card (re-loading media).
const CardsGalleriesCarouselInner = (props: any) => {
  const { projectInfo } = props;
  const finalEntry = props?.fields || props;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { setDownloadBrochureModalOpen, additionalInfo, settings } = useContext(ModalsContext);
  const [isSo, setIsSo] = useState<boolean>(false);
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const value = localStorage?.getItem("isSo");
      if (value) {
        setIsSo(value === "true");
      }
    }
  }, []);

  const isHomePage = pathname === "/" || pathname === `/${locale}/`;
  if (isSo && isHomePage) {
    return null;
  }

  return (
    <div
      className="flex flex-col w-full md:flex-row md:items-center overflow-hidden"
      style={
        {
          ...(finalEntry.backgroundColor && { backgroundColor: finalEntry.backgroundColor }),
          ...(finalEntry.textColor && { color: finalEntry.textColor, "--font-primary-color": finalEntry.textColor }),
          ...(finalEntry.scroll && { cursor: "url(/icons/autocarousel.svg) 23 23, auto" }),
        } as CSSProperties
      }
    >
      <div className={`block ${finalEntry.stickyDownloadBrochure ? "" : "md:hidden"}`}>
        {(finalEntry.overtitle || finalEntry.logo || finalEntry.brochure) && !finalEntry.stickyDownloadBrochure && (
          <div className="flex flex-row w-22\/24 mx-auto mt-xxl md:mt-0 md:w-[250px] md:flex-col md:items-center md:justify-center md:h-full">
            {(finalEntry.overtitle || finalEntry.logo) && (
              <div className="w-1/2 md:w-full text-center">
                {finalEntry.logo && (
                  <Image
                    className="max-w-[120px] md:max-w-[auto] md:inline-block mx-auto"
                    src={finalEntry.logo.url}
                    alt={finalEntry.logo.title}
                    width={finalEntry.logo.width}
                    height={finalEntry.logo.height}
                  />
                )}
              </div>
            )}
            {finalEntry.brochure && (
              <div className="w-1/2 text-right md:mt-l md:text-center md:w-full flex items-center justify-center">
                <Button
                  size={ButtonSize.SMALL}
                  theme={ButtonTheme.BLACK}
                  type="button"
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => {
                    brochureDowloadCtaClick({ cta_name: t("label.brochure"), ...additionalInfo });
                    settings.brochureData = { url: finalEntry.brochure?.url, projectInfo };
                    setDownloadBrochureModalOpen(true);
                  }}
                >
                  <span className="flex items-center uppercase gap-xs font-medium">
                    <Icon className="stroke-black" name="download" size="20" />
                    {t("label.brochure")}
                  </span>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={`w-full my-xl md:w-24\/24 cardgallery-swiper`}
        style={finalEntry.scroll ? { cursor: "url(/icons/autocarousel.svg) 23 23, auto" } : undefined}
      >
        <div className={`h-CardGallery md:ml-[calc(${!(finalEntry.overtitle || finalEntry.logo || finalEntry.brochure) && "100%_*_2_/_24"})]`}>
          <Swiper
            speed={1000}
            slidesPerView={"auto"}
            autoplay={
              finalEntry.scroll
                ? {
                    delay: 1000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            spaceBetween={isDesktop ? 16 : 8}
            navigation={{
              nextEl: `.swiper-button-next-${finalEntry.id}`,
              prevEl: `.swiper-button-prev-${finalEntry.id}`,
            }}
            pagination={{ type: "progressbar", el: `.swiper-pagination-carousel-${finalEntry.id}` }}
            watchSlidesProgress={true}
            slidesOffsetAfter={isDesktop ? 100 : 16}
            freeMode={finalEntry.scroll ? false : isDesktop ? false : { enabled: true }}
            mousewheel={
              finalEntry.scroll
                ? {
                    releaseOnEdges: true,
                    forceToAxis: false,
                  }
                : false
            }
            modules={[Navigation, Pagination, Mousewheel, FreeMode, Autoplay]}
          >
            {!finalEntry.stickyDownloadBrochure && isDesktop && (finalEntry.overtitle || finalEntry.logo || finalEntry.brochure) && (
              <SwiperSlide className="!w-fit flex flex-col justify-center !h-[630px] custom-slide">
                <div className="flex flex-row w-22\/24 mx-auto mt-xxl md:mt-0 md:w-[250px] md:flex-col md:items-center md:justify-center md:h-full">
                  {(finalEntry.overtitle || finalEntry.logo) && (
                    <div className="w-1/2 text-left md:text-center md:w-full">
                      {finalEntry.overtitle && (
                        <div className="hidden md:block md:uppercase font-primary text-s font-medium">{finalEntry.overtitle}</div>
                      )}
                      {finalEntry.logo && (
                        <Image
                          className="md:inline-block"
                          src={finalEntry.logo.url}
                          alt={finalEntry.logo.title}
                          width={finalEntry.logo.width}
                          height={finalEntry.logo.height}
                        />
                      )}
                    </div>
                  )}
                  {finalEntry.brochure && (
                    <div className="w-1/2 text-right md:mt-l md:text-center md:w-full flex items-center justify-center">
                      <Button
                        size={ButtonSize.MEDIUM}
                        customClass="border !py-[11px]"
                        theme={ButtonTheme.BLACK}
                        type="button"
                        variant={ButtonVariant.SECONDARY}
                        onClick={() => {
                          brochureDowloadCtaClick({ cta_name: t("label.brochure"), ...additionalInfo });
                          settings.brochureData = { url: finalEntry.brochure?.url, projectInfo };
                          setDownloadBrochureModalOpen(true);
                        }}
                      >
                        <span className="flex items-center uppercase gap-xs font-medium">
                          <Icon className="stroke-inherit" name="download" size="20" />
                          {t("label.brochure")}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            )}
            {finalEntry.cards.map((item: any, index: number) => (
              <SwiperSlide className={`!w-fit ${!isDesktop && "custom-slide"}`} key={index}>
                <CardGallery entry={item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <CardCarouselBottomNavigation carouselId={finalEntry.id} customClass="mt-xl" carouselType={finalEntry.type} />
      </div>
    </div>
  );
};

export default function CardsGalleriesCarousel({ entry, projectInfo }: Props) {
  const parsedExperiences = useMemo(() => parseCustomExperience(entry), [entry]);
  const passthroughProps = useMemo(() => ({ projectInfo }), [projectInfo]);

  return (
    <Experience
      {...entry}
      key={`experience-${entry.id}`}
      id={entry.id as any}
      component={CardsGalleriesCarouselInner as any}
      experiences={parsedExperiences}
      passthroughProps={passthroughProps as any}
    />
  );
}
